import { ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { MenuTopSkeleton } from '@components/layout/src/components/menu-top/menu-top-skeleton';
import { PresetsHomeSkeleton } from '@components/presets/presets-home-skeleton/src/presets-home-skeleton';
import { SelectVenueSkeleton } from '@components/select-venue-skeleton/src/select-venue-skeleton';
import { SidebarDesktopSkeleton } from '@components/sidebar-desktop/src/sidebar-desktop-skeleton';
import { AddVenueSkeleton } from '@pages/[companyId]/[clubSlug]/add-venue/add-venue-skeleton';
import { InfoSkeleton } from '@pages/[companyId]/[clubSlug]/company/info/Info-skeleton';
import { IntegrationsSkeleton } from '@pages/[companyId]/[clubSlug]/company/integrations/integrations-skeleton';
import { UsersSkeleton } from '@pages/[companyId]/[clubSlug]/company/users/users-skeleton';
import { ReportPageSkeleton } from '@pages/events/[companyId]/[clubSlug]/[eventId]/report-skeleton';

const DefaultLayoutShell = ({ children }: { children: ReactNode }) => (
  <div className='flex flex-col h-[calc(100dvh)] bg-secondary overflow-auto'>
    <MenuTopSkeleton />
    <div className='flex flex-row flex-1 overflow-hidden'>
      <div className='hidden lg:flex'>
        <SidebarDesktopSkeleton />
      </div>
      <main className='flex flex-col w-full overflow-hidden bg-secondary mx-auto'>{children}</main>
    </div>
  </div>
);

const WithoutSidebarShell = ({ children }: { children: ReactNode }) => (
  <div className='bg-primary min-h-screen'>
    <div className='max-w-7xl mx-auto pt-8 md:pt-14'>{children}</div>
  </div>
);

export const GenericContentSkeleton = () => (
  <div className='animate-pulse p-6 flex flex-col gap-6'>
    <div className='h-8 bg-tertiary rounded-sm w-1/3' />
    <div className='h-4 bg-tertiary rounded-sm w-2/3' />
    <div className='flex flex-col gap-4 mt-4'>
      <div className='h-24 bg-tertiary rounded-md w-full' />
      <div className='h-24 bg-tertiary rounded-md w-full' />
      <div className='h-24 bg-tertiary rounded-md w-full' />
    </div>
  </div>
);

const getSkeletonContent = (pathname: string): ReactNode => {
  if (pathname.endsWith('/report')) return <ReportPageSkeleton />;
  if (pathname.endsWith('/add-venue')) return <AddVenueSkeleton />;
  if (/\/company\/users(\/|$)/.test(pathname)) return <UsersSkeleton />;
  if (pathname.endsWith('/company/info')) return <InfoSkeleton />;
  if (pathname.endsWith('/company/integrations') || pathname.endsWith('/company/widget'))
    return <IntegrationsSkeleton />;
  if (/\/offers(\/|$)/.test(pathname)) return <PresetsHomeSkeleton />;
  return <GenericContentSkeleton />;
};

export const RouteContentSkeleton = () => {
  const { pathname } = useLocation();
  return <>{getSkeletonContent(pathname)}</>;
};

interface AuthLoadingSkeletonProps {
  children?: ReactNode;
}

export const AuthLoadingSkeleton = ({ children }: AuthLoadingSkeletonProps) => {
  const { pathname } = useLocation();

  // Select venue — without-sidebar page; use the shell so the loading/redirect skeleton
  // keeps the same top offset as the real page (LayoutWithoutSidebar) and doesn't jump.
  if (pathname.startsWith('/select-venue')) {
    return <WithoutSidebarShell>{children ?? <SelectVenueSkeleton />}</WithoutSidebarShell>;
  }

  // Without sidebar routes
  if (pathname.endsWith('/report')) {
    return <WithoutSidebarShell>{children ?? <ReportPageSkeleton />}</WithoutSidebarShell>;
  }

  if (pathname.endsWith('/add-venue')) {
    return <WithoutSidebarShell>{children ?? <AddVenueSkeleton />}</WithoutSidebarShell>;
  }

  if (pathname.endsWith('/info-point')) {
    return <WithoutSidebarShell>{children ?? <GenericContentSkeleton />}</WithoutSidebarShell>;
  }

  // Default layout routes — no shell during auth phase to avoid skeleton→real shell jump.
  // LayoutDefault already renders its own skeleton shell while company/club data loads.
  if (children) {
    return <DefaultLayoutShell>{children}</DefaultLayoutShell>;
  }

  return <div className='bg-secondary h-[calc(100dvh)]' />;
};
